import {
  isObservableArray,
  isObservableProp,
  isComputedProp,
  observable,
  observe,
  reaction,
  runInAction,
  untracked,
  type IReactionDisposer,
  type IObservableValue,
} from 'mobx';
import type { ITileManagerModel } from '@/interfaces/models';
import { TileInstance, isConstructedTileInstance } from '@/models/tiles/TileInstance';
import { isConstructedTileMeta } from '@/models/tiles/SignalSides';
import type { TileId } from '@/types/ids';
import { Side } from '@/schemas/tiles';
import {
  connectionLocationKey,
  registerConnectionTileOrder,
  type ConnectionTileIndex,
} from './ConnectionCalculator';
import { isConstructedTileCollection } from '@/models/TileManagerModel';

interface TileSnapshot {
  metaRef: TileInstance['meta'];
  tileType: string;
  chipInstanceId: string;
  x: number;
  y: number;
  rotation: number | null | undefined;
  ioConfig: string;
}

interface TrackedTile {
  id: TileId;
  tile: TileInstance;
  snapshot: TileSnapshot;
  group: TileGroup;
  order: number;
}
interface TileGroup {
  entries: Set<TrackedTile>;
  revision: IObservableValue<number>;
  dispose: () => void;
}
type IdentifiedTileSnapshot = TileSnapshot & { id: TileId };

// Inspect descriptors before snapshotting IO values. Native observable data
// fields retain the fast path; class/computed/custom accessors keep the original
// whole-array snapshot read boundary instead of being sampled by two scanners.
function ordinaryIoField(value: object, key: string): boolean {
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return (
    !!descriptor &&
    ('value' in descriptor ||
      (isObservableProp(value, key) && !isComputedProp(value, key)))
  );
}

function needsWholeSnapshot(tile: TileInstance): boolean {
  if (
    !isConstructedTileInstance(tile) ||
    Object.getPrototypeOf(tile) !== TileInstance.prototype ||
    !isConstructedTileMeta(tile.meta)
  )
    return true;
  const io = tile.effectiveIoSides;
  if (io == null) return false;
  if (typeof io !== 'object' || Object.getPrototypeOf(io) !== Object.prototype)
    return true;
  for (const side of Object.values(Side)) {
    if (!Object.hasOwn(io, side)) {
      if (side in io) return true;
      continue;
    }
    if (!ordinaryIoField(io, side)) return true;
    const entry = io[side];
    if (entry == null) continue;
    if (
      typeof entry !== 'object' ||
      Object.getPrototypeOf(entry) !== Object.prototype ||
      !ordinaryIoField(entry, 'type') ||
      !ordinaryIoField(entry, 'forceConnection')
    )
      return true;
  }
  return false;
}

const sameSnapshotValues = (a: TileSnapshot, b: TileSnapshot): boolean =>
  a.tileType === b.tileType &&
  a.chipInstanceId === b.chipInstanceId &&
  a.x === b.x &&
  a.y === b.y &&
  a.rotation === b.rotation &&
  a.ioConfig === b.ioConfig;

/**
 * Maintain connection snapshots and indexes for local edits. Up to 64 tiles
 * share reaction dependencies. An untracked geometry scan and IO override
 * refresh preserve discovery of external aliases at transaction boundaries.
 */
export class ConnectionTileTracker implements ConnectionTileIndex {
  readonly byPosition = new Map<string, TileInstance>();
  private readonly tracked = new Map<TileId, TrackedTile>();
  private readonly ioOverrides = new Set<TrackedTile>();
  private readonly groups = new Set<TileGroup>();
  private readonly availableGroups = new Set<TileGroup>();
  private readonly previous = new Map<TileId, TileSnapshot | undefined>();
  private readonly revision = observable.box(0);
  private readonly flushRevision = observable.box(0);
  private readonly collisionFallback = observable.box(false);
  private readonly disposeMembership: IReactionDisposer;
  private readonly disposeChanges: IReactionDisposer;
  private readonly disposeFlush: IReactionDisposer;
  private disposeArray?: () => void;
  private fallbackPrevious?: IdentifiedTileSnapshot[];
  private initialized = false;
  private scanPending = false;
  private orderDirty = false;
  private orderAvailable = false;
  private orderCollection?: TileInstance[];
  private manager?: ITileManagerModel;
  private readonly disposeOrder: () => void;

  constructor(
    manager: ITileManagerModel,
    readonly byId: Map<TileId, TileInstance>,
    private readonly onChange: (affected: Set<TileId>, removed: Set<TileId>) => void,
  ) {
    this.manager = manager;
    this.disposeOrder = registerConnectionTileOrder(this, (affected, tiles) => {
      if (
        !this.initialized ||
        !this.incremental ||
        !this.orderAvailable ||
        this.orderDirty ||
        tiles !== this.orderCollection
      )
        return undefined;
      const entries: TrackedTile[] = [];
      for (const id of affected) {
        const entry = this.tracked.get(id);
        if (entry) entries.push(entry);
      }
      entries.sort((a, b) => a.order - b.order);
      return entries.map((entry) => entry.tile);
    });
    // Membership can queue before geometry reactions in the same action. Stage
    // publication once so those already-queued tile snapshots join the delta.
    this.disposeChanges = reaction(
      () => this.revision.get(),
      () => this.flushRevision.set(this.flushRevision.get() + 1),
    );
    this.disposeFlush = reaction(
      () => this.flushRevision.get(),
      () => this.flush(),
    );
    this.disposeMembership = reaction(
      () => {
        const tiles = manager.tiles;
        // A custom plain collection cannot report membership edits. Retain the
        // old whole-collection dependency scan so its next observable tile edit
        // discovers external insertions/removals as the previous model did.
        // Check before reading IO getters. Short-circuit on unsupported IO
        // so enabling fallback does not invalidate this same initial snapshot.
        const whole =
          untracked(() => tiles.some(needsWholeSnapshot)) ||
          this.collisionFallback.get() ||
          !isConstructedTileCollection(manager, tiles);
        return {
          tiles,
          snapshots: whole
            ? tiles.map((tile) => ({ id: tile.id, ...this.snapshot(tile) }))
            : undefined,
        };
      },
      ({ tiles, snapshots }) => {
        if (snapshots) this.enterFallback();
        this.bind(tiles, snapshots);
      },
      { fireImmediately: true, equals: () => false },
    );
  }

  dispose(): void {
    this.disposeOrder();
    this.manager = undefined;
    this.orderAvailable = false;
    this.orderCollection = undefined;
    this.disposeMembership();
    this.disposeChanges();
    this.disposeFlush();
    this.disposeArray?.();
    this.disposeArray = undefined;
    for (const group of this.groups) group.dispose();
    this.groups.clear();
    this.availableGroups.clear();
    this.tracked.clear();
    this.ioOverrides.clear();
    this.previous.clear();
    this.byPosition.clear();
    this.fallbackPrevious = undefined;
  }

  get incremental(): boolean {
    return !this.collisionFallback.get();
  }

  private bind(tiles: TileInstance[], snapshots?: IdentifiedTileSnapshot[]): void {
    const wasInitialized = this.initialized;
    this.orderAvailable =
      this.manager !== undefined && isConstructedTileCollection(this.manager, tiles);
    this.orderCollection = this.orderAvailable ? tiles : undefined;
    this.orderDirty = this.orderAvailable;
    this.disposeArray?.();
    this.disposeArray = undefined;
    if (!this.incremental) {
      this.rebuildFallback(tiles, snapshots);
      return;
    }
    const next = new Set(tiles);
    for (const entry of this.tracked.values()) {
      if (!next.has(entry.tile)) this.remove(entry.tile);
    }
    const seenIds = new Set<TileId>();
    let order = 0;
    for (const tile of tiles) {
      if (seenIds.has(tile.id)) {
        this.enterFallback();
        return;
      }
      seenIds.add(tile.id);
      this.add(tile, true, order++);
      if (!this.incremental) return;
    }
    this.initialized = true;
    this.orderDirty = false;
    if (isObservableArray(tiles)) {
      this.disposeArray = observe(tiles, (change) => {
        // observe() itself is not transactional. A direct multi-item splice
        // must publish only after every removed and added tile is indexed.
        runInAction(() => {
          // Appending/removing the tail leaves every surviving rank intact.
          // Keep the full refresh for interior edits and reorderings.
          const tailEdit =
            change.type === 'splice' &&
            this.orderAvailable &&
            !this.orderDirty &&
            this.orderCollection === tiles &&
            this.tracked.size ===
              tiles.length - change.added.length + change.removed.length &&
            change.index + change.removed.length === this.tracked.size &&
            change.removed.every((tile, offset) => {
              const entry = this.tracked.get(tile.id);
              return (
                !!entry && entry.tile === tile && entry.order === change.index + offset
              );
            });
          this.orderDirty = this.orderAvailable && !tailEdit;
          if (change.type === 'splice') {
            for (const tile of change.removed) this.remove(tile);
            for (let index = 0; index < change.added.length; index++) {
              this.add(change.added[index], false, tailEdit ? change.index + index : -1);
              if (!this.incremental) break;
            }
          } else {
            this.remove(change.oldValue);
            this.add(change.newValue);
          }
          this.scheduleScan();
        });
      });
    }
    if (wasInitialized) this.scheduleScan();
  }

  private snapshot(tile: TileInstance): TileSnapshot {
    const meta = tile.meta;
    const position = tile.globalTilePosition;
    return {
      metaRef: meta,
      tileType: tile.tileType,
      chipInstanceId: position.chipInstanceId,
      x: tile.location.x,
      y: tile.location.y,
      rotation: meta.rotation as number | null | undefined,
      ioConfig: Object.values(Side)
        .map((side) => {
          const io = tile.effectiveIoSides?.[side];
          return io ? `${io.type}:${Number(io.forceConnection)}` : '';
        })
        .join('|'),
    };
  }

  private add(tile: TileInstance, alreadyPresent = false, order = -1): void {
    if (!this.incremental) return;
    if (alreadyPresent) {
      const entry = this.tracked.get(tile.id);
      if (entry?.tile === tile) {
        entry.order = order;
        return;
      }
    }
    const existing = this.tracked.get(tile.id);
    if (existing) {
      this.enterFallback();
      return;
    }
    if (needsWholeSnapshot(tile)) {
      if (this.initialized) this.queue(tile.id, undefined);
      this.enterFallback();
      return;
    }
    const snapshot = this.snapshot(tile);
    const group: TileGroup =
      this.availableGroups.values().next().value ?? this.createGroup();
    const entry: TrackedTile = { id: tile.id, tile, snapshot, group, order };
    group.entries.add(entry);
    if (group.entries.size >= 64) this.availableGroups.delete(group);
    this.tracked.set(tile.id, entry);
    this.trackIoOverride(entry);
    this.byId.set(tile.id, tile);
    if (this.initialized) this.queue(tile.id, undefined);
    this.updatePosition(tile);
    group.revision.set(group.revision.get() + 1);
  }

  private createGroup(): TileGroup {
    const group: TileGroup = {
      entries: new Set(),
      revision: observable.box(0),
      dispose: () => undefined,
    };
    group.dispose = reaction(
      () => {
        void group.revision.get();
        // A late IO assignment can queue this group before membership. Do not
        // evaluate the new getter here; the whole snapshot must read it once.
        if ([...group.entries].some((entry) => needsWholeSnapshot(entry.tile)))
          return undefined;
        return [...group.entries].map((entry) => ({
          entry,
          next: this.snapshot(entry.tile),
        }));
      },
      (changes) => {
        if (!this.incremental) return;
        if (!changes) {
          this.enterFallback();
          return;
        }
        for (const { entry, next } of changes) {
          const tile = entry.tile;
          // A membership observer may remove an already-queued entry.
          if (this.tracked.get(entry.id) !== entry) continue;
          if (tile.id !== entry.id) {
            this.enterFallback();
            return;
          }
          const previous = entry.snapshot;
          const changed = !sameSnapshotValues(previous, next);
          if (changed) this.queue(tile.id, previous);
          entry.snapshot = next;
          this.trackIoOverride(entry);
          if (previous.metaRef !== next.metaRef && !isConstructedTileMeta(next.metaRef)) {
            this.enterFallback();
            return;
          }
          if (changed) this.updatePosition(tile, previous);
          if (!this.incremental) return;
        }
        // Keep the previous global-reaction alias discovery after reference
        // replacements and changed-then-reset fields, even for a net-zero group.
        this.scheduleScan();
      },
      { equals: () => false, fireImmediately: true },
    );
    this.groups.add(group);
    this.availableGroups.add(group);
    return group;
  }

  private remove(tile: TileInstance): void {
    const entry = this.tracked.get(tile.id);
    if (!entry || entry.tile !== tile) {
      if (
        this.incremental &&
        [...this.tracked.values()].some((candidate) => candidate.tile === tile)
      )
        this.enterFallback();
      return;
    }
    const group = entry.group;
    group.entries.delete(entry);
    if (group.entries.size === 0) {
      group.dispose();
      this.groups.delete(group);
      this.availableGroups.delete(group);
    } else {
      this.availableGroups.add(group);
      group.revision.set(group.revision.get() + 1);
    }
    this.ioOverrides.delete(entry);
    this.tracked.delete(tile.id);
    this.byId.delete(tile.id);
    const previous = entry.snapshot;
    const oldKey = `${previous.chipInstanceId}:${previous.x},${previous.y}`;
    if (this.byPosition.get(oldKey) === tile) this.byPosition.delete(oldKey);
    const key = connectionLocationKey(tile);
    if (this.byPosition.get(key) === tile) this.byPosition.delete(key);
    this.queue(tile.id, previous);
  }

  private updatePosition(tile: TileInstance, previous?: TileSnapshot): void {
    if (previous) {
      const key = `${previous.chipInstanceId}:${previous.x},${previous.y}`;
      if (this.byPosition.get(key) === tile) this.byPosition.delete(key);
    }
    const key = connectionLocationKey(tile);
    const existing = this.byPosition.get(key);
    // Batched swaps may still have an old index entry, while the object has
    // already moved. Only a collision in the actual current collection falls back.
    if (existing && existing !== tile && connectionLocationKey(existing) === key) {
      this.enterFallback();
      return;
    }
    this.byPosition.set(key, tile);
  }

  private rebuildFallback(
    tiles: TileInstance[],
    observed?: IdentifiedTileSnapshot[],
  ): void {
    const previous = this.fallbackPrevious;
    const next =
      observed ?? tiles.map((tile) => ({ id: tile.id, ...this.snapshot(tile) }));
    this.fallbackPrevious = next;
    this.previous.clear();
    for (const group of this.groups) group.dispose();
    this.groups.clear();
    this.availableGroups.clear();
    this.tracked.clear();
    this.ioOverrides.clear();
    this.byId.clear();
    this.byPosition.clear();
    for (const tile of tiles) {
      this.byId.set(tile.id, tile);
      this.byPosition.set(connectionLocationKey(tile), tile);
    }
    this.initialized = true;
    if (!previous) return;
    const nextById = new Map(next.map((tile) => [tile.id, tile]));
    const previousById = new Map(previous.map((tile) => [tile.id, tile]));
    const changed = new Set<TileId>();
    for (const [id, tile] of nextById) {
      const old = previousById.get(id);
      if (!old || !sameSnapshotValues(old, tile)) changed.add(id);
    }
    const removed = new Set([...previousById.keys()].filter((id) => !nextById.has(id)));
    for (const id of removed) changed.add(id);
    // Retain the original whole-snapshot delta behavior for ambiguous public
    // array mutations, including its last-entry-wins ID/position maps.
    const previousPositions = new Map(
      previous.map((tile) => [`${tile.x},${tile.y}`, tile.id]),
    );
    const nextPositions = new Map(next.map((tile) => [`${tile.x},${tile.y}`, tile.id]));
    const affected = new Set<TileId>();
    for (const id of changed) {
      for (const [snapshot, positions] of [
        [previousById.get(id), previousPositions],
        [nextById.get(id), nextPositions],
      ] as const) {
        if (!snapshot) continue;
        for (const [dx, dy] of [
          [0, 0],
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ]) {
          const neighbor = positions.get(`${snapshot.x + dx},${snapshot.y + dy}`);
          if (neighbor) affected.add(neighbor);
        }
      }
    }
    if (affected.size || removed.size) this.onChange(affected, removed);
  }

  private enterFallback(): void {
    if (!this.incremental) return;
    if (this.initialized) {
      const snapshots = new Map(
        [...this.tracked].map(([id, entry]) => [id, entry.snapshot]),
      );
      for (const [id, previous] of this.previous) {
        if (previous) snapshots.set(id, previous);
        else snapshots.delete(id);
      }
      this.fallbackPrevious = [...snapshots].map(([id, snapshot]) => ({
        id,
        ...snapshot,
      }));
    }
    this.collisionFallback.set(true);
  }

  private queue(tileId: TileId, previous: TileSnapshot | undefined): void {
    if (!this.previous.has(tileId)) this.previous.set(tileId, previous);
    this.scheduleScan();
  }

  private scheduleScan(): void {
    if (this.scanPending) return;
    this.scanPending = true;
    runInAction(() => this.revision.set(this.revision.get() + 1));
  }

  private collectGeometryAliases(): void {
    for (const entry of this.tracked.values()) {
      const id = entry.id;
      if (entry.tile.id !== id) {
        this.enterFallback();
        return;
      }
      const position = entry.tile.globalTilePosition;
      const previous = entry.snapshot;
      const x = entry.tile.location.x;
      const y = entry.tile.location.y;
      if (
        previous.x === x &&
        previous.y === y &&
        previous.chipInstanceId === position.chipInstanceId
      )
        continue;
      if (!this.previous.has(id)) this.previous.set(id, previous);
      entry.snapshot = { ...previous, x, y, chipInstanceId: position.chipInstanceId };
      this.updatePosition(entry.tile, previous);
      if (!this.incremental) return;
    }
  }

  private trackIoOverride(entry: TrackedTile): void {
    if (entry.tile.effectiveIoSides) this.ioOverrides.add(entry);
    else this.ioOverrides.delete(entry);
  }

  private collectIoAliases(): void {
    // The public IO records may contain class getters backed by external state.
    // Like position aliases, the old global snapshot rediscovered those values
    // when any observed tile changed. Only tiles with overrides need this scan.
    for (const entry of this.ioOverrides) {
      const ioConfig = Object.values(Side)
        .map((side) => {
          const io = entry.tile.effectiveIoSides?.[side];
          return io ? `${io.type}:${Number(io.forceConnection)}` : '';
        })
        .join('|');
      const previous = entry.snapshot;
      if (previous.ioConfig === ioConfig) continue;
      if (!this.previous.has(entry.id)) this.previous.set(entry.id, previous);
      entry.snapshot = { ...previous, ioConfig };
    }
  }

  private includeNeighbors(snapshot: TileSnapshot, affected: Set<TileId>): void {
    const { chipInstanceId, x, y } = snapshot;
    for (const [dx, dy] of [
      [0, 0],
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ]) {
      const tile = this.byPosition.get(`${chipInstanceId}:${x + dx},${y + dy}`);
      if (tile) affected.add(tile.id);
    }
  }

  private flush(): void {
    if (!this.incremental) return;
    this.scanPending = false;
    this.collectGeometryAliases();
    if (!this.incremental) return;
    this.collectIoAliases();
    this.refreshOrder();
    if (!this.previous.size) return;
    const previous = new Map(this.previous);
    this.previous.clear();
    // Membership observers see intermediate mutations inside an action. The
    // original whole-collection snapshot only recalculated a net change, so an
    // equivalent replacement or remove/reinsert must preserve temporary manual
    // disconnections until a later connection-relevant edit.
    for (const [id, old] of previous) {
      const next = this.tracked.get(id)?.snapshot;
      if ((!old && !next) || (old && next && sameSnapshotValues(old, next)))
        previous.delete(id);
    }
    if (!previous.size) return;
    const affected = new Set<TileId>();
    const removed = new Set<TileId>();
    for (const [tileId, snapshot] of previous) {
      affected.add(tileId);
      if (snapshot) this.includeNeighbors(snapshot, affected);
    }
    // Remove all old positions before inserting destinations, including swaps.
    for (const [tileId, snapshot] of previous) {
      if (!snapshot) continue;
      const key = `${snapshot.chipInstanceId}:${snapshot.x},${snapshot.y}`;
      if (this.byPosition.get(key)?.id === tileId) this.byPosition.delete(key);
    }
    for (const [tileId] of previous) {
      const entry = this.tracked.get(tileId);
      if (entry) this.byPosition.set(connectionLocationKey(entry.tile), entry.tile);
      else removed.add(tileId);
    }
    for (const [tileId] of previous) {
      const entry = this.tracked.get(tileId);
      if (entry) this.includeNeighbors(entry.snapshot, affected);
    }
    this.onChange(affected, removed);
  }

  private refreshOrder(): void {
    if (!this.orderDirty || !this.orderAvailable) return;
    const manager = this.manager;
    if (!manager) {
      this.orderAvailable = false;
      return;
    }
    const tiles = manager.tiles;
    if (tiles !== this.orderCollection || !isConstructedTileCollection(manager, tiles)) {
      this.orderAvailable = false;
      this.orderCollection = undefined;
      return;
    }
    // Internal records are not observable. Clearing their ranks first detects
    // duplicate membership without allocating another collection-sized map.
    for (const entry of this.tracked.values()) entry.order = -1;
    let order = 0;
    for (const tile of tiles) {
      const entry = this.tracked.get(tile.id);
      if (!entry || entry.tile !== tile || entry.order !== -1) {
        this.orderAvailable = false;
        return;
      }
      entry.order = order++;
    }
    this.orderAvailable = order === this.tracked.size;
    if (this.orderAvailable) this.orderDirty = false;
  }
}
